import { useState } from 'react';
import {
  Anchor,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link } from 'react-router';
import {
  useCiktiIndir,
  useContractors,
  useKapilar,
  useMyAuthorization,
  usePersonsSayfali,
} from '../api/queries';
import { KISI_EKSIKLERI, KISI_EKSIK_ETIKETI, KISI_EKSIK_SAGLIK_KOVALARI } from '@isg/validation';
import type { KisiEksik } from '@isg/validation';
import { menuSatiriGorunur } from '../shell/menu';
import { KIMLIK_TIPI_ETIKET } from '../i18n/tr';
import { PersonelYuklemePaneli } from './PersonelYuklemePaneli';
import { Sayfalama, KapiSebebi } from '../sozlesme';
import { BosListeNotu, QueryStatus } from './parts';

/**
 * ÇALIŞANLAR (F1·S10; madde 37 kapanışı 05.08.2026; **ad + süzgeç F4·S5·B1**).
 *
 * ✏️ **T7 — "Kişiler" adı "Çalışanlar" oldu ve ad BİR SÜZGEÇ KARARINI ZORUNLU
 * KILDI** (IA §3/1): ekrandaki kayıt KİŞİDİR — işten ayrılmışlar, hiç
 * istihdamı olmayanlar ve yüklenici personeli de aynı listededir (kişi ≠
 * istihdam kilidi, §2.7). Başlık "Çalışanlar" olduğuna göre **varsayılan
 * süzgeç "aktif istihdamı olanlar"** olmak zorundadır. *Adı değiştirip
 * süzgeci değiştirmemek en kötü seçenektir — başlık o satırlar için yalan
 * söyler.*
 *
 * ⚠️ Süzgeç **KALDIRILABİLİR** (kullanıcı ayrılmışları görebilmeli) ve
 * **SUNUCUDA uygulanır** — ekran ikinci filtre yazmaz (§2.16).
 *
 * ⚠️ **YÜKLENİCİ PERSONELİ VARSAYILANDA DA GÖRÜNÜR:** aktif istihdamı olan
 * herkes bu listededir. Onları dışlayan kural `isyeriCalisaniKosulu`dur ve
 * o SAYIM kuralıdır (§3.2), liste kuralı değil — karara bağlanmadan
 * daraltılmaz.
 *
 * Aşağısı ilk sürümün notudur: Kimlik no MASKELİ
 * ALANDAN gösterilir — API liste yanıtına tam değer zaten binmez; tam gösterim
 * yalnız kişi kartındaki loglu akıştadır. DOĞUM TARİHİ kolonu YOK (S10 gezinti
 * kararı: veri şemada durur, görünürlüğü kaza modülüyle [Faz 3] döner).
 * Liste bahçe filtrelidir: bahçedeki bir işverende istihdamı olan kişiler.
 *
 * F1.5·S12: toplu yükleme paneli — PORTALDAKİYLE AYNI bileşen, tek farkla:
 * iç ekranda işveren SEÇİLİR (şirket VEYA yüklenici; temsilcide oturumdan
 * türetilir). Buton görünürlüğü ≠ yetki: seçim bahçe dışıysa API 403 döner.
 */
const ISTIHDAM_SUZGECI = [
  { value: 'aktif', label: 'Aktif istihdamı olanlar' },
  { value: 'hepsi', label: 'Tümü (ayrılmışlar dahil)' },
] as const;

const SAYFA_BOYUTU = 25;

export function PersonsScreen(): React.JSX.Element {
  const [istihdam, setIstihdam] = useState<'aktif' | 'hepsi'>('aktif');
  // ✏️ **TUR 9 / B4 (T9-04): SAYFALAMA + ARAMA SUNUCUDA.** Liste dört yüz
  // kayıtta tek istekte iniyordu; arama da yoktu. Toplam GÖRÜNÜR (P4) —
  // çıktı motorunun *"şu anki süzgeçle N kayıt"* cümlesi buradan kurulur.
  const [arama, setArama] = useState('');
  const [sayfa, setSayfa] = useState(1);
  // ✏️ MADDE 248 / K-248: EKSİK SÜZGECİ — veri girişini sıraya sokar. Kova
  // SUNUCUDA, uygunluk motorunun yüklemiyle (ekran hesaplamaz).
  const [eksik, setEksik] = useState<KisiEksik | null>(null);
  // ⚠️ Sağlık kovası sağlık OKUMA sınırına tabidir (§2.3) — seçenek yalnız o
  // şapkalara çizilir; sunucu da ayrıca 403 verir (görünür + 403 olmasın).
  const authz = useMyAuthorization();
  const saglikGorur = authz.isSuccess && menuSatiriGorunur(authz.data.sapkalar, 'saglik');
  const eksikSecenekleri = KISI_EKSIKLERI.filter(
    (kova) => saglikGorur || !KISI_EKSIK_SAGLIK_KOVALARI.includes(kova),
  );
  const persons = usePersonsSayfali({
    istihdam,
    eksik,
    q: arama.trim(),
    sayfa,
    sayfaBoyutu: SAYFA_BOYUTU,
  });
  const satirlar = persons.data?.satirlar ?? [];
  const indir = useCiktiIndir();
  // P13-b (madde 225): iki kapı SUNUCUDAN (`GET kapilar`) — kişi açma §20.5/A1 ·
  // toplu aktarma S9 satırı; ekran şapka dizgesi yazmaz.
  const kapilar = useKapilar();
  const yukleyebilir = kapilar.data?.kisiYuklemeKapisi.yazabilir ?? false;
  const kisiAcabilir = kapilar.data?.kisiKapisi.yazabilir ?? false;
  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Title order={3}>Çalışanlar</Title>
        <Group align="flex-end">
          {/* ⚠️ P1-a ŞERHİ: liste SAYFALANIYOR — soluk satır son sayfanın
              sonuna düşerdi. Kişi zaten BAĞIMSIZ bir kayıttır (kendi sayfası
              var) → ekleme ayrı sayfadan yürür (P1). */}
          {kisiAcabilir && (
            <Button component={Link} to="/calisanlar/yeni" data-testid="kisi-ekle-ac">
              Kişi ekle
            </Button>
          )}
          {kapilar.data && (
            <KapiSebebi
              durum={kapilar.data.kisiKapisi}
              nesne="Kişiyi"
              fiil="açar"
              testId="kisi-ekle-yazamama-sebebi"
            />
          )}
          <TextInput
            data-testid="calisan-arama"
            label="Ad ara"
            placeholder="Ad soyad"
            value={arama}
            onChange={(e) => {
              setArama(e.currentTarget.value);
              setSayfa(1);
            }}
            w={220}
          />
          <Select
            data-testid="istihdam-suzgeci"
            label="Görünüm"
            allowDeselect={false}
            value={istihdam}
            onChange={(deger) => {
              setIstihdam(deger === 'hepsi' ? 'hepsi' : 'aktif');
              setSayfa(1);
            }}
            data={[...ISTIHDAM_SUZGECI]}
            w={260}
          />
          <Select
            data-testid="eksik-suzgeci"
            label="Eksik"
            allowDeselect={false}
            value={eksik ?? 'hepsi'}
            onChange={(deger) => {
              setEksik(deger && deger !== 'hepsi' ? (deger as KisiEksik) : null);
              setSayfa(1);
            }}
            data={[
              { value: 'hepsi', label: 'Tümü' },
              ...eksikSecenekleri.map((kova) => ({ value: kova, label: KISI_EKSIK_ETIKETI[kova] })),
            ]}
            w={200}
          />
          {/* ⚠️ ÇIKTI EKRANDAKİ SÜZGEÇLE ÇALIŞIR (P4): aynı parametre uca
              gider ve sunucu AYNI sorguyu koşar — "312 kayıt" ne diyorsa
              dosyada o kadar satır olur. Süzgeçsiz tam döküm yolu YOKTUR.
              Yetki = okuma yetkisi: bu ekranı gören çıktısını alır. */}
          <Button
            variant="default"
            data-testid="calisan-excel"
            loading={indir.isPending}
            onClick={() =>
              indir.mutate({
                yol: `/api/persons/export?istihdam=${istihdam}${eksik ? `&eksik=${eksik}` : ''}`,
                yedekAd: 'calisanlar.xlsx',
              })
            }
          >
            Excel'e aktar
          </Button>
        </Group>
      </Group>
      <QueryStatus queries={[persons]} />
      {yukleyebilir && <TopluYuklemeBolumu />}
      {kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.kisiYuklemeKapisi}
          nesne="Personeli toplu"
          fiil="yükler"
          testId="kisi-yukleme-yazamama-sebebi"
        />
      )}
      {persons.data && satirlar.length === 0 && (
        <BosListeNotu
          mesaj={
            eksik !== null
              ? 'Bu süzgeçte kişi yok.'
              : istihdam === 'aktif'
                ? 'Bahçenizde aktif istihdamı olan kişi yok. "Tümü" görünümü ayrılmışları da getirir.'
                : 'Bahçenizde görüntülenecek kişi yok.'
          }
        />
      )}
      {satirlar.length > 0 && (
        <>
          <Table striped highlightOnHover data-testid="kisi-listesi">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ad soyad</Table.Th>
                <Table.Th>Kimlik</Table.Th>
                <Table.Th>Sicil no</Table.Th>
                <Table.Th>E-posta</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {satirlar.map((person) => (
                <Table.Tr key={person.id} data-testid="kisi-satiri">
                  <Table.Td>
                    <Anchor component={Link} to={`/calisanlar/${person.id}`} size="sm">
                      {person.adSoyad}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    {KIMLIK_TIPI_ETIKET[person.kimlikTipi]} · {person.kimlikNoMaskeli}
                  </Table.Td>
                  <Table.Td>{person.personelSicilNo ?? '—'}</Table.Td>
                  <Table.Td>{person.email ?? '—'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Sayfalama
            sayfa={persons.data?.sayfa ?? 1}
            sayfaBoyutu={persons.data?.sayfaBoyutu ?? SAYFA_BOYUTU}
            toplam={persons.data?.toplam ?? 0}
            onDegis={setSayfa}
            testId="calisan-sayfalama"
          />
          <Text size="xs" c="dimmed">
            Kimlik numaraları sunucuda maskelenir; tam gösterim kişi kartında yetkiye tabidir ve
            kayıt altına alınır. Arama yalnız ad üzerinde çalışır.
          </Text>
        </>
      )}
    </Stack>
  );
}

/**
 * İç taraf toplu yükleme (F1.5·S12): şablon portaldakiyle AYNIDIR.
 *
 * ✏️ F4·S3 (madde 80): ŞİRKET SEÇİMİ KALKTI — işveren şirketi satırdaki
 * **İşyeri SGK Sicil** kolonundan türetilir. Seçim yalnız *"ana firma mı,
 * yüklenici mi"* sorusunu cevaplar; şirket seçtirmek aynı bilgiyi ikinci kez
 * sormak ve dosyayla çelişebilmek olurdu (tek okuyucu ilkesi).
 */
const ANA_FIRMA = 'ana-firma';

function TopluYuklemeBolumu(): React.JSX.Element {
  const contractors = useContractors();
  const [secim, setSecim] = useState<string>(ANA_FIRMA);

  return (
    <Card withBorder padding="md" data-testid="toplu-yukleme">
      <Stack gap="sm">
        <Text fw={600}>Toplu personel yükleme</Text>
        <Select
          data-testid="isveren-secimi"
          label="Personel kimin?"
          description="Şirket, satırdaki işyerinin SGK sicilinden türetilir."
          allowDeselect={false}
          searchable
          value={secim}
          onChange={(deger) => setSecim(deger ?? ANA_FIRMA)}
          data={[
            { value: ANA_FIRMA, label: 'Ana firma personeli' },
            ...(contractors.data ?? []).map((contractor) => ({
              value: contractor.id,
              label: `Yüklenici — ${contractor.ad}`,
            })),
          ]}
          w={320}
        />
        <PersonelYuklemePaneli contractorId={secim === ANA_FIRMA ? null : secim} />
      </Stack>
    </Card>
  );
}
